import { useCallback, useState } from 'react';
import { useLegacyAuthGatewayFetch } from '@penta/penta-api';
import { useClient } from '../contexts/ClientContext';
import { toSafeRedirectUrl } from '../utils/safeRedirect';

const REDIRECT_DELAY_MS = 2000;

export const useRedirectToOldIb = () => {
  const legacyApi = useLegacyAuthGatewayFetch();
  const { selectedClientId } = useClient();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const redirect = useCallback(
    async (pageIdentifier: string, productId?: number) => {
      if (selectedClientId == null) {
        console.error('[OldIB] No client ID available for redirect');
        return;
      }

      setIsRedirecting(true);

      try {
        // `productId` nastavuje ve starém IB kontext účtu — bez něj se historie
        // i platba otevřou bez vazby na konkrétní účet. V integračním dokumentu
        // banky k v4.51.0 pole není; dorazilo samostatným zadáním přes Slack
        // (14. 5. 2026, historie a platby běžného, spořicího a úvěrového účtu).
        // Legacy bránu banka nespecifikuje — `LegacyAuthGatewayAPI-swagger.yaml`
        // je náš přepis, takže dohodu nedoloží nic než tenhle komentář a
        // `docs/Integrace FE Internet Bankingu.md`.
        const { data, error } = await legacyApi.POST('/v1/url', {
          body: {
            pageIdentifier,
            clientId: String(selectedClientId),
            ...(productId !== undefined ? { productId } : {}),
          },
        });

        if (error || !data) {
          console.error('[OldIB] Failed to get redirect URL', error);
          setIsRedirecting(false);
          return;
        }

        // `data.url` přichází z odpovědi serveru. Blokuje se jen nepovolené
        // schéma — `javascript:` by se ze same-origin skriptu vykonalo. Cizí
        // doména se jen loguje (viz safeRedirect).
        const target = toSafeRedirectUrl(data.url);
        if (!target) {
          console.error(
            '[OldIB] Odmítnut cíl přesměrování s nepovoleným schématem',
          );
          setIsRedirecting(false);
          return;
        }
        // Přes searchParams, ne konkatenací — kód se tím korektně zakóduje.
        target.searchParams.set('code', data.code);

        await new Promise((resolve) => setTimeout(resolve, REDIRECT_DELAY_MS));
        window.location.href = target.toString();
      } catch (err) {
        console.error('[OldIB] Unexpected error during redirect', err);
        setIsRedirecting(false);
      }
    },
    [legacyApi, selectedClientId],
  );

  return { redirect, isRedirecting };
};
